import React, { useState } from 'react';
import { Site, supabase, Hygienist, Client } from '../../lib/supabase';
import { uploadFile } from '../../utils/fileUpload';
import { geocodeAddress } from '../../utils/geocodingService';
import { AddressAutocomplete } from './AddressAutocomplete';
import { GPSVerificationLink } from './GPSVerificationLink';
import { X, Building2, Calendar, MapPin, FileText, Plus, Minus, Zap, Flame, Shield, TestTube, Wind, User, Phone, AlertTriangle, Upload, Download, Trash2, RefreshCw, Users } from 'lucide-react';

interface SiteFormProps {
  site?: Site | null;
  onSubmit: (siteData: Partial<Site>, projectStatusData?: any) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

export const SiteForm: React.FC<SiteFormProps> = ({ site, onSubmit, onClose, loading = false }) => {
  const [commonRequirements, setCommonRequirements] = useState<string[]>([]);
  const [loadingRequirements, setLoadingRequirements] = useState(true);
  const [hygienists, setHygienists] = useState<Hygienist[]>([]);
  const [loadingHygienists, setLoadingHygienists] = useState(true);
  const [loadingProjectStatus, setLoadingProjectStatus] = useState(!!site);
  const [showAddHygienist, setShowAddHygienist] = useState(false);
  const [newHygienist, setNewHygienist] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [quoteFile, setQuoteFile] = useState<File | null>(null);
  const [processingSpreadsheet, setProcessingSpreadsheet] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [coordinateSource, setCoordinateSource] = useState<'auto' | 'manual'>('manual');
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [showAddClient, setShowAddClient] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientModified, setClientModified] = useState(false);

  const [formData, setFormData] = useState({
    name: site?.name || '',
    type: site?.type || 'demolition',
    address: site?.address || '',
    start_date: site?.start_date || '',
    end_date: site?.end_date || '',
    description: site?.description || '',
    requirements: site?.requirements?.join(', ') || '',
    photo_url: site?.photo_url || '',
    status: site?.status || 'active',
    asbestos_type: site?.asbestos_type || 'none',
    quote_url: site?.quote_url || '',
    latitude: site?.latitude?.toString() || '',
    longitude: site?.longitude?.toString() || '',
    geofence_radius: site?.geofence_radius?.toString() || '750',
    client_id: site?.client_id || '',
    client_name: site?.client_name || '',
    client_address: site?.client_address || '',
    client_phone: site?.client_phone || '',
    principal: site?.principal || ''
  });

  const [projectStatus, setProjectStatus] = useState({
    gas_approval: 'live',
    electrical_approval: 'live',
    water_approval: 'live',
    sewer_approval: 'live',
    council_approval: 'pending',
    epa_permit: 'not_required',
    safework_notification: 'pending',
    safework_expiry_date: site?.safework_expiry_date || '',
    temporary_fence: 'pending',
    air_monitoring: 'not_required',
    soil_testing: 'not_required',
    hygienist_id: '',
    hygienist_name: '',
    hygienist_phone: '',
    gas_approval_notes: '',
    electrical_approval_notes: '',
    water_approval_notes: '',
    sewer_approval_notes: '',
    council_approval_notes: '',
    epa_permit_notes: '',
    safework_notification_notes: '',
    temporary_fence_notes: '',
    air_monitoring_notes: '',
    soil_testing_notes: ''
  });

  const [selectedRequirements, setSelectedRequirements] = useState<string[]>(
    site?.requirements || []
  );
  const [customRequirement, setCustomRequirement] = useState('');

  // Fetch existing requirements from database
  React.useEffect(() => {
    const fetchRequirements = async () => {
      try {
        const { data, error } = await supabase
          .from('sites')
          .select('requirements')
          .not('requirements', 'is', null);

        if (error) throw error;

        // Extract all unique requirements from all sites
        const allRequirements = new Set<string>();
        data?.forEach(site => {
          if (site.requirements && Array.isArray(site.requirements)) {
            site.requirements.forEach(req => {
              if (req && req.trim()) {
                allRequirements.add(req.trim());
              }
            });
          }
        });

        // Convert to sorted array
        const sortedRequirements = Array.from(allRequirements).sort();
        setCommonRequirements(sortedRequirements);
      } catch (error) {
        console.error('Error fetching requirements:', error);
        // Fallback to basic requirements if database fetch fails
        setCommonRequirements([
          'Hard hat',
          'High-visibility vest',
          'Safety boots',
          'Safety glasses',
          'White card',
          'First aid certification'
        ]);
      } finally {
        setLoadingRequirements(false);
      }
    };

    fetchRequirements();
    fetchHygienists();
    fetchClients();

    // Set selected client if editing existing site
    if (site?.client_id) {
      setSelectedClientId(site.client_id);
    }

    // Fetch project status if editing existing site
    if (site?.id) {
      fetchProjectStatus();
    } else {
      setLoadingProjectStatus(false);
    }
  }, []);

  React.useEffect(() => {
    if (showAddClient && (formData.client_name || formData.client_address || formData.client_phone)) {
      const principalInfo = [
        formData.client_name,
        formData.client_address,
        formData.client_phone
      ].filter(Boolean).join('\n');

      setFormData(prev => ({
        ...prev,
        principal: principalInfo
      }));
    }
  }, [formData.client_name, formData.client_address, formData.client_phone, showAddClient]);

  const fetchProjectStatus = async () => {
    if (!site?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('project_status')
        .select('*')
        .eq('site_id', site.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching project status:', error);
      } else if (data) {
        // Update project status with fetched data
        setProjectStatus({
          gas_approval: data.gas_approval || 'live',
          electrical_approval: data.electrical_approval || 'live',
          council_approval: data.council_approval || 'pending',
          epa_permit: data.epa_permit || (site.type === 'asbestos_removal' ? 'pending' : 'not_required'),
          safework_notification: data.safework_notification || 'pending',
          safework_expiry_date: site?.safework_expiry_date || '',
          temporary_fence: data.temporary_fence || 'pending',
          air_monitoring: data.air_monitoring || (site.type === 'asbestos_removal' ? 'required' : 'not_required'),
          soil_testing: data.soil_testing || 'not_required',
          hygienist_id: data.hygienist_id || '',
          hygienist_name: data.hygienist_name || '',
          hygienist_phone: data.hygienist_phone || ''
        });
      } else {
        // No project status exists, set defaults based on site type
        setProjectStatus({
          gas_approval: 'live',
          electrical_approval: 'live',
          council_approval: 'pending',
          epa_permit: site.type === 'asbestos_removal' ? 'pending' : 'not_required',
          safework_notification: 'pending',
          safework_expiry_date: site?.safework_expiry_date || '',
          temporary_fence: 'pending',
          air_monitoring: site.type === 'asbestos_removal' ? 'required' : 'not_required',
          soil_testing: 'not_required',
          hygienist_id: '',
          hygienist_name: '',
          hygienist_phone: ''
        });
      }
    } catch (error) {
      console.error('Error fetching project status:', error);
    } finally {
      setLoadingProjectStatus(false);
    }
  };

  const fetchHygienists = async () => {
    try {
      const { data, error } = await supabase
        .from('hygienists')
        .select('*')
        .order('name');

      if (error) throw error;
      setHygienists(data || []);
    } catch (error) {
      console.error('Error fetching hygienists:', error);
    } finally {
      setLoadingHygienists(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleAddHygienist = async () => {
    if (!newHygienist.name.trim() || !newHygienist.phone.trim()) {
      alert('Name and phone number are required');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('hygienists')
        .insert([newHygienist])
        .select()
        .single();

      if (error) throw error;

      setHygienists(prev => [...prev, data]);
      setNewHygienist({ name: '', phone: '', email: '', company: '' });
      setShowAddHygienist(false);

      // Auto-select the newly added hygienist
      setProjectStatus(prev => ({
        ...prev,
        hygienist_id: data.id,
        hygienist_name: data.name,
        hygienist_phone: data.phone
      }));
    } catch (error) {
      console.error('Error adding hygienist:', error);
      alert('Failed to add hygienist. Please try again.');
    }
  };

  const handleClientChange = async (clientId: string) => {
    if (clientId === 'new') {
      setShowAddClient(true);
      setSelectedClientId('');
      setClientModified(false);
      setFormData(prev => ({
        ...prev,
        client_id: '',
        client_name: '',
        client_address: '',
        client_phone: '',
        principal: ''
      }));
      return;
    }

    setSelectedClientId(clientId);
    setClientModified(false);

    if (!clientId) {
      setFormData(prev => ({
        ...prev,
        client_id: '',
        client_name: '',
        client_address: '',
        client_phone: '',
        principal: ''
      }));
      return;
    }

    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient) {
      const principalInfo = [
        selectedClient.name,
        selectedClient.address,
        selectedClient.phone
      ].filter(Boolean).join('\n');

      setFormData(prev => ({
        ...prev,
        client_id: clientId,
        client_name: selectedClient.name,
        client_address: selectedClient.address || '',
        client_phone: selectedClient.phone || '',
        principal: principalInfo
      }));
    }
  };

  const handleSaveNewClient = async () => {
    if (!formData.client_name.trim()) {
      alert('Client name is required');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('You must be logged in to add a client');
        return;
      }

      const { data, error } = await supabase
        .from('clients')
        .insert([{
          name: formData.client_name,
          address: formData.client_address || null,
          phone: formData.client_phone || null,
          user_id: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      const principalInfo = [
        data.name,
        data.address,
        data.phone
      ].filter(Boolean).join('\n');

      setClients(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedClientId(data.id);
      setFormData(prev => ({
        ...prev,
        client_id: data.id,
        principal: principalInfo
      }));
      setShowAddClient(false);
      setClientModified(false);
      alert('Client saved successfully!');
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Failed to save client. Please try again.');
    }
  };

  const handleUpdateClient = async () => {
    if (!selectedClientId || !formData.client_name.trim()) {
      alert('Client name is required');
      return;
    }

    try {
      const { error } = await supabase
        .from('clients')
        .update({
          name: formData.client_name,
          address: formData.client_address || null,
          phone: formData.client_phone || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedClientId);

      if (error) throw error;

      const principalInfo = [
        formData.client_name,
        formData.client_address,
        formData.client_phone
      ].filter(Boolean).join('\n');

      // Update clients list
      setClients(prev => prev.map(c =>
        c.id === selectedClientId
          ? { ...c, name: formData.client_name, address: formData.client_address || null, phone: formData.client_phone || null }
          : c
      ).sort((a, b) => a.name.localeCompare(b.name)));

      setFormData(prev => ({
        ...prev,
        principal: principalInfo
      }));

      setClientModified(false);
      alert('Client updated successfully!');
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client. Please try again.');
    }
  };

  const handleQuoteUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'text/csv', // .csv
      'application/pdf' // .pdf
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload an Excel file (.xls, .xlsx), CSV file, or PDF');
      return;
    }

    setUploadingQuote(true);
    try {
      const result = await uploadFile(file, 'quote', 'site-quotes');
      
      if (result.success && result.url) {
        setFormData(prev => ({ ...prev, quote_url: result.url! }));
        setQuoteFile(file);
        
        // If we're editing an existing site, automatically process the spreadsheet
        if (site?.id) {
          alert('Quote file uploaded successfully! Processing spreadsheet...');
          await processSpreadsheet(site.id, result.url);
        } else {
          alert('Quote file uploaded successfully! Spreadsheet will be processed when the site is saved.');
        }
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading quote file:', error);
      alert(`Failed to upload quote file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingQuote(false);
    }
  };

  const handleQuoteFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleQuoteUpload(e.target.files[0]);
    }
  };

  const removeQuoteFile = () => {
    setFormData(prev => ({ ...prev, quote_url: '' }));
    setQuoteFile(null);
  };

  const processSpreadsheet = async (siteId: string, quoteUrl: string) => {
    setProcessingSpreadsheet(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('process-excel-quote', {
        body: {
          site_id: siteId,
          quote_url: quoteUrl
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to process spreadsheet');
      }

      if (data?.success) {
        alert(`Success! Processed ${data.processed_quantities} waste quantities from spreadsheet.`);
      } else {
        throw new Error(data?.error || 'Unknown error processing spreadsheet');
      }
    } catch (error) {
      console.error('Error processing spreadsheet:', error);
      alert(`Failed to process spreadsheet: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingSpreadsheet(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Remove 'principal' field as it's only for display, not stored in database
    const { principal, ...formDataWithoutPrincipal } = formData;

    const siteData = {
      ...formDataWithoutPrincipal,
      requirements: selectedRequirements,
      latitude: formData.latitude ? Number(formData.latitude) : null,
      longitude: formData.longitude ? Number(formData.longitude) : null,
      geofence_radius: formData.geofence_radius ? Number(formData.geofence_radius) : 750,
      safework_expiry_date: projectStatus.safework_expiry_date || null,
      // Convert empty string to null for UUID field to prevent Supabase errors
      client_id: formData.client_id || null
    };

    // Remove safework_expiry_date from projectStatus as it belongs to sites table
    const { safework_expiry_date, ...projectStatusWithoutSafework } = projectStatus;
    const cleanProjectStatus = {
      ...projectStatusWithoutSafework,
      hygienist_id: projectStatus.hygienist_id || null
    };

    try {
      // Pass both site data and project status data to the parent
      await onSubmit(siteData, cleanProjectStatus);

      // If there's a quote file uploaded and we have a site ID, process the spreadsheet
      if (formData.quote_url && (site?.id || siteData.id)) {
        const siteId = site?.id || siteData.id;
        if (siteId) {
          await processSpreadsheet(siteId, formData.quote_url);
        }
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      throw error; // Re-throw to let parent handle the error
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));

    // Track if client information has been modified
    if (['client_name', 'client_address', 'client_phone'].includes(e.target.name) && selectedClientId && !showAddClient) {
      setClientModified(true);
    }

    // Auto-set air monitoring to required when friable asbestos is selected
    if (e.target.name === 'asbestos_type' && e.target.value === 'friable') {
      setProjectStatus(prev => ({
        ...prev,
        air_monitoring: 'required'
      }));
    }
  };

  const handleProjectStatusChange = (field: string, value: string) => {
    setProjectStatus(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleHygienistChange = (hygienistId: string) => {
    const selectedHygienist = hygienists.find(h => h.id === hygienistId);
    if (selectedHygienist) {
      setProjectStatus(prev => ({
        ...prev,
        hygienist_id: hygienistId,
        hygienist_name: selectedHygienist.name,
        hygienist_phone: selectedHygienist.phone
      }));
    } else {
      setProjectStatus(prev => ({
        ...prev,
        hygienist_id: '',
        hygienist_name: '',
        hygienist_phone: ''
      }));
    }
  };

  const toggleRequirement = (requirement: string) => {
    setSelectedRequirements(prev => 
      prev.includes(requirement)
        ? prev.filter(req => req !== requirement)
        : [...prev, requirement]
    );
  };

  const addCustomRequirement = () => {
    if (customRequirement.trim() && !selectedRequirements.includes(customRequirement.trim())) {
      setSelectedRequirements(prev => [...prev, customRequirement.trim()]);
      setCustomRequirement('');
    }
  };

  const removeRequirement = (requirement: string) => {
    setSelectedRequirements(prev => prev.filter(req => req !== requirement));
  };

  const handleAddressChange = (address: string) => {
    setFormData(prev => ({
      ...prev,
      address
    }));
  };

  const handleCoordinatesSelect = (latitude: number, longitude: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(4),
      longitude: longitude.toFixed(4)
    }));
    setCoordinateSource('auto');
  };

  const handleManualCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setCoordinateSource('manual');
  };

  const handleRegeocode = async () => {
    if (!formData.address.trim()) {
      alert('Please enter an address first');
      return;
    }

    setGeocoding(true);
    try {
      const result = await geocodeAddress(formData.address);
      if (result) {
        setFormData(prev => ({
          ...prev,
          latitude: result.latitude.toFixed(4),
          longitude: result.longitude.toFixed(4)
        }));
        setCoordinateSource('auto');
        alert('GPS coordinates updated successfully!');
      } else {
        alert('Unable to find coordinates for this address. Please enter them manually.');
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      alert('Failed to geocode address. Please try again or enter coordinates manually.');
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-orange-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              {site ? 'Edit Site' : 'Add New Site'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Name *
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Enter site name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Type *
              </label>
              <select
                name="type"
                required
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="demolition">Demolition</option>
                <option value="asbestos_removal">Asbestos Removal</option>
                <option value="contaminated_soil">Contaminated Soil</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asbestos Type
              </label>
              <select
                name="asbestos_type"
                value={formData.asbestos_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="none">None</option>
                <option value="friable">Friable</option>
                <option value="non_friable">Non-Friable</option>
                <option value="mixed">Mixed (Friable & Non-Friable)</option>
                <option value="unknown">Unknown - Testing Required</option>
              </select>
              {formData.asbestos_type === 'friable' && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  High-risk friable asbestos detected - Air monitoring automatically set to required
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="inline h-4 w-4 mr-1" />
              Address *
            </label>
            <AddressAutocomplete
              value={formData.address}
              onChange={handleAddressChange}
              onCoordinatesSelect={handleCoordinatesSelect}
              placeholder="Start typing address..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Start typing to search for Australian addresses. GPS coordinates will be filled automatically.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Start Date *
              </label>
              <input
                type="date"
                name="start_date"
                required
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                End Date *
              </label>
              <input
                type="date"
                name="end_date"
                required
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Description
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Site description and notes"
            />
          </div>

          {/* Client Information */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2 text-blue-600" />
                Client Information
              </h4>
            </div>

            {loadingClients ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-sm text-gray-600">Loading clients...</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Client
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => handleClientChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">No client selected</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}{client.phone ? ` - ${client.phone}` : ''}
                      </option>
                    ))}
                    <option value="new">+ Add New Client</option>
                  </select>
                </div>

                {(showAddClient || selectedClientId) && (
                  <div className="grid grid-cols-1 gap-4 p-4 bg-white border border-gray-200 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Client Name {showAddClient && '*'}
                      </label>
                      <input
                        type="text"
                        name="client_name"
                        value={formData.client_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter client name or company name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Client Address
                      </label>
                      <input
                        type="text"
                        name="client_address"
                        value={formData.client_address}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter client address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="inline h-4 w-4 mr-1" />
                        Client Phone
                      </label>
                      <input
                        type="tel"
                        name="client_phone"
                        value={formData.client_phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter client phone number"
                      />
                    </div>

                    {showAddClient && (
                      <div className="flex space-x-3 pt-2">
                        <button
                          type="button"
                          onClick={handleSaveNewClient}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Save Client
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddClient(false);
                            setClientModified(false);
                            setFormData(prev => ({
                              ...prev,
                              client_name: '',
                              client_address: '',
                              client_phone: ''
                            }));
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {selectedClientId && !showAddClient && clientModified && (
                      <div className="flex space-x-3 pt-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handleUpdateClient}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                          Update Client
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const selectedClient = clients.find(c => c.id === selectedClientId);
                            if (selectedClient) {
                              setFormData(prev => ({
                                ...prev,
                                client_name: selectedClient.name,
                                client_address: selectedClient.address || '',
                                client_phone: selectedClient.phone || ''
                              }));
                              setClientModified(false);
                            }
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          Discard Changes
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {selectedClientId && !showAddClient && !clientModified && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-blue-900 text-sm">{formData.client_name}</p>
                          {formData.client_address && (
                            <p className="text-xs text-blue-700 flex items-center mt-1">
                              <MapPin className="h-3 w-3 mr-1" />
                              {formData.client_address}
                            </p>
                          )}
                          {formData.client_phone && (
                            <p className="text-xs text-blue-700 flex items-center mt-1">
                              <Phone className="h-3 w-3 mr-1" />
                              {formData.client_phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      Edit the client information above to update this client for all sites
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Project Approvals & Utilities */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Zap className="h-5 w-5 mr-2 text-orange-600" />
              Project Approvals & Utilities
            </h4>
            
            {loadingProjectStatus ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                <span className="ml-2 text-sm text-gray-600">Loading project status...</span>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Utilities */}
              <div className="space-y-4">
                <h5 className="font-medium text-gray-800 flex items-center">
                  <Zap className="h-4 w-4 mr-2 text-yellow-600" />
                  Utilities & Services
                </h5>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Flame className="inline h-4 w-4 mr-1 text-blue-600" />
                      Gas Status
                    </label>
                    <select
                      value={projectStatus.gas_approval}
                      onChange={(e) => handleProjectStatusChange('gas_approval', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="live">Live</option>
                      <option value="pending">Pending</option>
                      <option value="disconnected">Disconnected</option>
                      <option value="not_applicable">Not Applicable</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Zap className="inline h-4 w-4 mr-1 text-yellow-600" />
                      Electrical Status
                    </label>
                    <select
                      value={projectStatus.electrical_approval}
                      onChange={(e) => handleProjectStatusChange('electrical_approval', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="live">Live</option>
                      <option value="pending">Pending</option>
                      <option value="disconnected">Disconnected</option>
                      <option value="not_applicable">Not Applicable</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Approvals */}
              <div className="space-y-4">
                <h5 className="font-medium text-gray-800 flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  Regulatory Approvals
                </h5>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building2 className="inline h-4 w-4 mr-1 text-purple-600" />
                      Council Approval
                    </label>
                    <select
                      value={projectStatus.council_approval}
                      onChange={(e) => handleProjectStatusChange('council_approval', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="not_applicable">Not Applicable</option>
                    </select>
                  </div>

                  {formData.type === 'asbestos_removal' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Shield className="inline h-4 w-4 mr-1 text-red-600" />
                        EPA Permit
                      </label>
                      <select
                        value={projectStatus.epa_permit}
                        onChange={(e) => handleProjectStatusChange('epa_permit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="not_required">Not Required</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FileText className="inline h-4 w-4 mr-1 text-orange-600" />
                      SafeWork Notification
                    </label>
                    <select
                      value={projectStatus.safework_notification}
                      onChange={(e) => handleProjectStatusChange('safework_notification', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1 text-orange-600" />
                      SafeWork Expiry Date
                    </label>
                    <input
                      type="date"
                      value={projectStatus.safework_expiry_date}
                      onChange={(e) => handleProjectStatusChange('safework_expiry_date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">When the SafeWork notification expires</p>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Safety & Environmental Controls */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-green-600" />
              Safety & Environmental Controls
            </h4>
            
            {loadingProjectStatus ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
                <span className="ml-2 text-sm text-gray-600">Loading safety controls...</span>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Site Safety */}
              <div className="space-y-4">
                <h5 className="font-medium text-gray-800 flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-green-600" />
                  Site Safety
                </h5>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site Fencing
                  </label>
                  <select
                    value={projectStatus.temporary_fence}
                    onChange={(e) => handleProjectStatusChange('temporary_fence', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="installed">Temp Fence</option>
                    <option value="cyclone_fence">Cyclone Fence</option>
                  </select>
                </div>
              </div>

              {/* Environmental */}
              <div className="space-y-4">
                <h5 className="font-medium text-gray-800 flex items-center">
                  <TestTube className="h-4 w-4 mr-2 text-green-600" />
                  Environmental
                </h5>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Wind className="inline h-4 w-4 mr-1 text-blue-600" />
                      Air Monitoring
                    </label>
                    <select
                      value={projectStatus.air_monitoring}
                      onChange={(e) => handleProjectStatusChange('air_monitoring', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="required">Required</option>
                      <option value="not_required">Not Required</option>
                    </select>
                    {formData.asbestos_type === 'friable' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                          <span className="text-xs font-medium text-red-800">
                            Air monitoring is mandatory for friable asbestos sites
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <TestTube className="inline h-4 w-4 mr-1 text-green-600" />
                      Soil Test Pits
                    </label>
                    <select
                      value={projectStatus.soil_testing}
                      onChange={(e) => handleProjectStatusChange('soil_testing', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="required">Required</option>
                      <option value="not_required">Not Required</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Nominated Hygienist */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Nominated Hygienist
              </h4>
              <button
                type="button"
                onClick={() => setShowAddHygienist(true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="h-3 w-3" />
                <span>Add New</span>
              </button>
            </div>
            
            {loadingProjectStatus || loadingHygienists ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-sm text-gray-600">Loading hygienist information...</span>
              </div>
            ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Hygienist</label>
                  <select
                    value={projectStatus.hygienist_id}
                    onChange={(e) => handleHygienistChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">No hygienist required</option>
                    {hygienists.map(hygienist => (
                      <option key={hygienist.id} value={hygienist.id}>
                        {hygienist.name} - {hygienist.phone} {hygienist.company ? `(${hygienist.company})` : ''}
                      </option>
                    ))}
                  </select>
              </div>
              
              {showAddHygienist && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-3">Add New Hygienist</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={newHygienist.name}
                        onChange={(e) => setNewHygienist(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Dr. John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        value={newHygienist.phone}
                        onChange={(e) => setNewHygienist(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+61 2 9999 9999"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={newHygienist.email}
                        onChange={(e) => setNewHygienist(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="john@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <input
                        type="text"
                        value={newHygienist.company}
                        onChange={(e) => setNewHygienist(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Hygiene Consulting Ltd"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3 mt-4">
                    <button
                      type="button"
                      onClick={handleAddHygienist}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Add Hygienist
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddHygienist(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              {projectStatus.hygienist_name && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-900 text-sm">{projectStatus.hygienist_name}</p>
                      <div className="flex items-center space-x-2 text-xs text-blue-700">
                        <Phone className="h-3 w-3" />
                        <span>{projectStatus.hygienist_phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Safety Requirements *
            </label>
            
            {/* Selected Requirements Display */}
            {selectedRequirements.length > 0 && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm font-medium text-orange-900 mb-2">Selected Requirements ({selectedRequirements.length}):</p>
                <div className="flex flex-wrap gap-2">
                  {selectedRequirements.map((req, index) => (
                    <span key={index} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs flex items-center space-x-1">
                      <span>{req}</span>
                      <button
                        type="button"
                        onClick={() => removeRequirement(req)}
                        className="text-orange-600 hover:text-orange-800"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Common Requirements Checklist */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Safety Requirements from Database ({commonRequirements.length} available):
              </p>
              {loadingRequirements ? (
                <div className="flex items-center justify-center p-8 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading requirements...</span>
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {commonRequirements.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {commonRequirements.map((requirement, index) => (
                        <label key={index} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-white rounded p-1">
                          <input
                            type="checkbox"
                            checked={selectedRequirements.includes(requirement)}
                            onChange={() => toggleRequirement(requirement)}
                            className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-gray-700">{requirement}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">No requirements found in database.</p>
                      <p className="text-xs text-gray-400 mt-1">Add custom requirements below to get started.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Custom Requirement Input */}
            <div className="flex space-x-2">
              <input
                type="text"
                value={customRequirement}
                onChange={(e) => setCustomRequirement(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Add custom requirement..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomRequirement())}
              />
              <button
                type="button"
                onClick={addCustomRequirement}
                disabled={!customRequirement.trim()}
                className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
              >
                <Plus className="h-4 w-4" />
                <span>Add</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Select from existing requirements above or add new requirements specific to this site
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo URL
            </label>
            <input
              type="url"
              name="photo_url"
              value={formData.photo_url}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="https://example.com/site-photo.jpg"
            />
          </div>

          {/* Quote/Spreadsheet Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Quote/Spreadsheet File
            </label>
            
            {formData.quote_url ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-900">
                        {quoteFile ? quoteFile.name : 'Quote file uploaded'}
                      </p>
                      <p className="text-xs text-green-700">
                        File ready for download
                        {site?.id && ' • Spreadsheet will be processed automatically'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => window.open(formData.quote_url, '_blank')}
                      className="p-2 text-green-600 hover:text-green-800 rounded-full hover:bg-green-100"
                      title="Download file"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={removeQuoteFile}
                      className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-100"
                      title="Remove file"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <div className="flex flex-col items-center space-y-3">
                  <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <Upload className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Upload quote or spreadsheet</p>
                    <p className="text-xs text-gray-500 mt-1">Excel (.xls, .xlsx), CSV, or PDF files</p>
                  </div>
                  <input
                    type="file"
                    accept=".xls,.xlsx,.csv,.pdf"
                    onChange={handleQuoteFileSelect}
                    className="hidden"
                    id="quote-upload"
                    disabled={uploadingQuote}
                  />
                  <label
                    htmlFor="quote-upload"
                    className="cursor-pointer bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors text-sm disabled:opacity-50"
                  >
                    {uploadingQuote ? 'Uploading...' : 'Choose File'}
                  </label>
                </div>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              Upload project quotes, waste quantity estimates, or related spreadsheets.
              {!site?.id && ' Spreadsheet will be automatically processed after saving the site.'}
              {site?.id && ' Spreadsheet will be processed immediately after upload.'}
            </p>
            
            {processingSpreadsheet && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-blue-800">Processing spreadsheet and extracting waste quantities...</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          {/* GPS Coordinates */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900 flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                GPS Coordinates (Optional)
              </h4>
              {formData.address && (
                <button
                  type="button"
                  onClick={handleRegeocode}
                  disabled={geocoding}
                  className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${geocoding ? 'animate-spin' : ''}`} />
                  <span>{geocoding ? 'Geocoding...' : 'Get Coordinates'}</span>
                </button>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-4">
              GPS coordinates are auto-filled when you select an address. Click "Get Coordinates" to regenerate or adjust manually if needed.
            </p>

            {formData.latitude && formData.longitude && (
              <div className="mb-4">
                <GPSVerificationLink
                  latitude={parseFloat(formData.latitude)}
                  longitude={parseFloat(formData.longitude)}
                />
                {coordinateSource === 'auto' && (
                  <p className="text-xs text-green-600 mt-2 flex items-center">
                    <MapPin className="h-3 w-3 mr-1" />
                    Coordinates auto-generated from address
                  </p>
                )}
                {coordinateSource === 'manual' && formData.latitude && formData.longitude && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Coordinates manually entered or adjusted
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude
                </label>
                <input
                  type="number"
                  name="latitude"
                  step="0.0001"
                  min="-90"
                  max="90"
                  value={formData.latitude}
                  onChange={handleManualCoordinateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="-33.8688"
                />
                <p className="text-xs text-gray-500 mt-1">4 decimal places</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude
                </label>
                <input
                  type="number"
                  name="longitude"
                  step="0.0001"
                  min="-180"
                  max="180"
                  value={formData.longitude}
                  onChange={handleManualCoordinateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="151.2093"
                />
                <p className="text-xs text-gray-500 mt-1">4 decimal places</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Geofence Radius (meters)
                </label>
                <input
                  type="number"
                  name="geofence_radius"
                  min="1"
                  max="1000"
                  value={formData.geofence_radius}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="750"
                />
                <p className="text-xs text-gray-500 mt-1">Worker check-in radius (1-1000m)</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> GPS coordinates are used for precise worker location validation during check-in/check-out. 
                The geofence radius determines how close workers need to be to the site to clock in.
              </p>
            </div>
          </div>
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || processingSpreadsheet}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving...' : 
               processingSpreadsheet ? 'Processing...' : 
               (site ? 'Update Site' : 'Create Site')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};